/**
 * Table options, as a panel beside the page.
 *
 * A PANEL AND NOT A DIALOG, which is Google's arrangement and the right one: these are settings you
 * adjust while LOOKING at the table, and a modal over the document would hide the thing being
 * changed. It sits where the version history sits, so the page narrows rather than being covered.
 *
 * EVERY CONTROL WRITES AN ATTRIBUTE ON THE DOCUMENT, and the rendition reads the same attributes --
 * see TableOptions.ts. Nothing here is a stylesheet-only effect, because a border weight that
 * vanished from the signed PDF would be worse than no control at all.
 *
 * THE UNBUILT ONES ARE PRESENT AND SAY WHY. Quick layouts, column data type and a minimum row
 * height are Google's and are not ours yet; a person who knows this panel should not have to wonder
 * whether they have missed something.
 */
import React from "react";
import {
    Box, Checkbox, Divider, FormControlLabel, IconButton, MenuItem, Select, TextField,
    ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from "@mui/material";

import ColourPicker from "./ColourPicker";
import Symbol from "./Symbol";
import { LAYOUT_ALIGNS, LAYOUT_WIDTHS, TableLook } from "./TableOptions";
import { KINDS, KIND_LABEL } from "./columnTypes";
import type { ColumnKind, ColumnType } from "./columnTypes";

const Grey = "#525968";

const BORDER_WEIGHTS = [0, 0.5, 0.75, 1, 1.5, 2, 3];

function Section({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <Box sx={{ mb: 2.5 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#202124", mb: 1 }}>
                {title}
            </Typography>
            <Box sx={{ backgroundColor: "#F8F9FB", borderRadius: 1.5, p: 1.5 }}>{children}</Box>
        </Box>
    );
}

function Label({ children }: { children: React.ReactNode }) {
    return <Typography sx={{ fontSize: 12, color: Grey, mb: 0.5 }}>{children}</Typography>;
}

/** A number with a unit, the shape Google uses for every measurement in this panel. */
function Measure({ value, unit, onChange, disabled, step = 0.1 }: {
    value: number | null; unit: string; onChange: (next: number) => void;
    disabled?: boolean; step?: number;
}) {
    const [draft, setDraft] = React.useState(String(value ?? ""));
    React.useEffect(() => { setDraft(String(value ?? "")); }, [value]);
    const commit = () => {
        const parsed = parseFloat(draft);
        if (Number.isFinite(parsed)) onChange(Math.max(0, parsed));
        else setDraft(String(value ?? ""));
    };
    return (
        <TextField size="small" value={draft} disabled={disabled}
                   onChange={(e) => setDraft(e.target.value)}
                   onBlur={commit}
                   onKeyDown={(e) => { if (e.key === "Enter") { commit(); (e.target as HTMLElement).blur(); } }}
                   slotProps={{
                       htmlInput: { inputMode: "decimal", step,
                                    style: { fontSize: 13, padding: "6px 8px", width: 56 } },
                       input: { endAdornment: <Typography sx={{ fontSize: 12, color: Grey, pl: 0.5 }}>{unit}</Typography> },
                   }}/>
    );
}

export default function TableOptionsPanel({
    look, columnWidthInches, verticalAlign, cellBackground, hasHeaderRow, editable, border,
    onLook, onColumnWidth, onVerticalAlign, onCellBackground, onPinHeader, onQuickLayout,
    onBorder, minRowHeight, onMinRowHeight, columnType, onColumnType, onClose,
}: {
    look: TableLook;
    columnWidthInches: number | null;
    verticalAlign: string | null;
    cellBackground: string | null;
    hasHeaderRow: boolean;
    editable: boolean;
    onLook: (next: Partial<TableLook>) => void;
    onColumnWidth: (inches: number | null) => void;
    columnType: ColumnType;
    onColumnType: (type: ColumnType) => void;
    onVerticalAlign: (value: string) => void;
    onCellBackground: (value: string | null) => void;
    onPinHeader: () => void;
    onQuickLayout: (fraction: number, align: "left" | "center" | "right") => void;
    /** The edge the person has clicked, if any, and what that edge currently is. */
    border: { edge: string; width: number; colour: string } | null;
    /** Change the picked edge. Only called while `border` is set. */
    onBorder: (width: number | null, colour: string | null) => void;
    /** The row the caret is in, in inches, or null when it has no minimum. */
    minRowHeight?: number | null;
    onMinRowHeight: (inches: number | null) => void;
    onClose: () => void;
}) {
    const unbuilt = (reason: string, children: React.ReactNode) => (
        <Tooltip title={reason} placement="left"><span>{children}</span></Tooltip>
    );

    return (
        <Box sx={{
            width: 320, flexShrink: 0, maxHeight: "calc(100vh - 160px)", overflowY: "auto",
            border: "1px solid #D8DDE4", borderRadius: 2, p: 2, backgroundColor: "#FFFFFF",
            position: "sticky", top: 16,
        }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Symbol name="table" size={20} colour="#202124"/>
                <Typography sx={{ fontSize: 16, flexGrow: 1 }}>Table options</Typography>
                <IconButton size="small" onClick={onClose} aria-label="Close table options">
                    <Symbol name="close" size={18} colour={Grey}/>
                </IconButton>
            </Box>

            {/* THE THUMBNAILS ARE DRAWN, NOT DEPICTED. Each one is the layout it applies, at
                scale: a bar of the right width in the right position, between two lines of
                "text" -- so choosing one is choosing the picture rather than decoding a label. */}
            <Box sx={{ mb: 2.5 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#202124", mb: 1 }}>
                    Quick layouts
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1 }}>
                    {LAYOUT_WIDTHS.map(([widthName, fraction]) => LAYOUT_ALIGNS.map((align) => (
                        <Tooltip key={`${widthName}-${align}`} placement="left"
                                 title={`${widthName}, ${align === "center" ? "centred" : align}`}>
                            {/* A CLICKABLE BOX WITH NO NAME IS UNREACHABLE: role and aria-label
                                make each thumbnail a real control, which a keyboard and a screen
                                reader need and which is also the only thing a test can aim at. */}
                            <Box role="button" tabIndex={editable ? 0 : -1}
                                 aria-label={`${widthName}, ${align === "center" ? "centred" : align}`}
                                 onClick={() => editable && onQuickLayout(fraction, align)}
                                 onKeyDown={(e) => { if (editable && (e.key === "Enter" || e.key === " ")) {
                                     e.preventDefault(); onQuickLayout(fraction, align); } }}
                                 sx={{
                                     p: 0.75, borderRadius: 1, cursor: editable ? "pointer" : "default",
                                     border: "1px solid #D8DDE4", backgroundColor: "#FFFFFF",
                                     "&:hover": { borderColor: editable ? "#1498D8" : "#D8DDE4" },
                                 }}>
                                <Box sx={{ height: 3, backgroundColor: "#E4E7EB", borderRadius: 2, mb: 0.5 }}/>
                                <Box sx={{ display: "flex",
                                           justifyContent: align === "center" ? "center"
                                                         : align === "right" ? "flex-end" : "flex-start" }}>
                                    <Box sx={{ width: `${fraction * 100}%`, height: 22, borderRadius: 0.5,
                                               border: "1.5px solid #1498D8" }}/>
                                </Box>
                                <Box sx={{ height: 3, backgroundColor: "#E4E7EB", borderRadius: 2, mt: 0.5 }}/>
                            </Box>
                        </Tooltip>
                    )))}
                </Box>
            </Box>

            <Section title="Table">
                <Label>Alignment</Label>
                <ToggleButtonGroup exclusive size="small" value={look.align ?? "left"} disabled={!editable}
                                   onChange={(_, value) => value && onLook({ align: value })}
                                   sx={{ mb: 1.5 }}>
                    {["left", "center", "right"].map((value) => (
                        <ToggleButton key={value} value={value} sx={{ px: 1.25, py: 0.5 }}>
                            <Symbol name={`format_align_${value}`} size={18}/>
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>

                <Label>Left indent (inches)</Label>
                <Measure value={look.indent} unit="in" disabled={!editable}
                         onChange={(next) => onLook({ indent: next })}/>
            </Section>

            <Section title="Column">
                <Label>Column width</Label>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Measure value={columnWidthInches} unit="in" disabled={!editable} step={0.05}
                             onChange={(next) => onColumnWidth(next || null)}/>
                    {columnWidthInches !== null && (
                        <Typography onClick={() => editable && onColumnWidth(null)}
                                    sx={{ fontSize: 12, color: "#1498D8", cursor: "pointer" }}>
                            Reset
                        </Typography>
                    )}
                </Box>
                <Divider sx={{ my: 1.5 }}/>
                {/* WHAT MAY BE WRITTEN IN THIS COLUMN. A constraint, not a format -- so the
                    server decides and this only offers it: cells that break the rule are marked as
                    you type, and the document cannot be SENT FOR APPROVAL while any remain.
                    EU GMP Annex 11 §6 asks for a check on critical data entered by hand; this is
                    where that check is declared. */}
                <Label>Column data type</Label>
                <Select size="small" fullWidth disabled={!editable}
                        value={columnType?.kind ?? "none"}
                        sx={{ fontSize: 13 }}
                        onChange={(e) => {
                            const kind = e.target.value as string;
                            if (kind === "none") return onColumnType(null);
                            if (kind === "choice") {
                                return onColumnType({ kind: "choice", values: columnType?.values ?? [] });
                            }
                            onColumnType({ kind: kind as ColumnKind });
                        }}>
                    <MenuItem value="none">Anything</MenuItem>
                    {KINDS.map((kind) => (
                        <MenuItem key={kind} value={kind}>{KIND_LABEL[kind]}</MenuItem>
                    ))}
                </Select>

                {columnType?.kind === "choice" && (
                    <>
                        {/* ONE PER LINE rather than comma-separated, because a permitted value may
                            itself contain a comma -- "Pass, with deviation" is a real result. */}
                        <Label>Allowed values, one per line</Label>
                        <TextField multiline minRows={3} size="small" fullWidth disabled={!editable}
                                   value={(columnType.values ?? []).join("\n")}
                                   onChange={(e) => onColumnType({
                                       kind: "choice",
                                       values: e.target.value.split("\n").map((v) => v.trim()).filter(Boolean),
                                   })}
                                   slotProps={{ htmlInput: { style: { fontSize: 13 } } }}/>
                        {!(columnType.values ?? []).length && (
                            <Typography sx={{ fontSize: 11, color: Grey, mt: 0.5 }}>
                                An empty list allows anything.
                            </Typography>
                        )}
                    </>
                )}
                {columnType?.kind === "date" && (
                    <Typography sx={{ fontSize: 11, color: Grey, mt: 0.5 }}>
                        2026-10-06 or 6 Oct 2026. A slashed date is refused: 01/02/2026 means two
                        different days either side of the Atlantic.
                    </Typography>
                )}
            </Section>

            <Section title="Row">
                <FormControlLabel
                    control={<Checkbox size="small" checked={hasHeaderRow} disabled={!editable}
                                       onChange={onPinHeader}/>}
                    label={<Typography sx={{ fontSize: 13 }}>Pin header row(s)</Typography>}/>
                <Typography sx={{ fontSize: 11, color: Grey, ml: 4, mt: -0.5, mb: 1 }}>
                    Repeated at the top of every page the table runs onto.
                </Typography>
                <FormControlLabel
                    control={<Checkbox size="small" checked={look.overflowRows} disabled={!editable}
                                       onChange={(e) => onLook({ overflowRows: e.target.checked })}/>}
                    label={<Typography sx={{ fontSize: 13 }}>Allow row to overflow across pages</Typography>}/>
                <Divider sx={{ my: 1.5 }}/>
                {/* A FLOOR, NOT A HEIGHT, and the label says "minimum" for that reason: the row
                    grows past it when its content needs more. This is how a signature block is
                    drawn in every procedure anybody has signed -- a row tall enough to write in,
                    whatever is typed into it. Zero clears it. */}
                <Label>Minimum row height</Label>
                <Measure value={minRowHeight ?? 0} unit="in" disabled={!editable}
                         onChange={(value) => onMinRowHeight(value > 0 ? value : null)}/>
            </Section>

            <Section title="Cell">
                <Label>Cell vertical alignment</Label>
                <Select size="small" fullWidth disabled={!editable}
                        value={verticalAlign ?? "top"}
                        onChange={(e) => onVerticalAlign(String(e.target.value))}
                        sx={{ fontSize: 13, mb: 1.5 }}>
                    {["top", "middle", "bottom"].map((value) => (
                        <MenuItem key={value} value={value} sx={{ fontSize: 13 }}>
                            {value[0].toUpperCase() + value.slice(1)}
                        </MenuItem>
                    ))}
                </Select>
                <Label>Cell padding</Label>
                <Measure value={look.cellPadding} unit="pt" disabled={!editable} step={1}
                         onChange={(next) => onLook({ cellPadding: next })}/>
            </Section>

            <Section title="Color">
                {/* WHAT THE CONTROLS ARE ABOUT RIGHT NOW. A weight that silently applied to the
                    whole table when somebody had just clicked one edge would be the worst of both:
                    they pointed at a rule and the app changed twelve. */}
                <Label>{border ? `Border — ${border.edge} edge of this cell` : "Table border"}</Label>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
                    <ColourPicker label="Table border colour" disabled={!editable}
                                  value={border ? border.colour : look.borderColor}
                                  onChange={(next) => border ? onBorder(border.width, next)
                                                             : onLook({ borderColor: next })}/>
                    <Select size="small" disabled={!editable}
                            value={border ? border.width : (look.borderWidth ?? 0.75)}
                            onChange={(e) => border ? onBorder(Number(e.target.value), border.colour)
                                                    : onLook({ borderWidth: Number(e.target.value) })}
                            sx={{ fontSize: 13, minWidth: 86 }}>
                        {BORDER_WEIGHTS.map((weight) => (
                            <MenuItem key={weight} value={weight} sx={{ fontSize: 13 }}>
                                {weight === 0 ? "None" : `${weight} pt`}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
                <Typography sx={{ fontSize: 11, color: Grey, mb: 1.5 }}>
                    {border ? "Click away from a border to go back to the whole table."
                            : "Click a cell border in the table to change just that one."}
                </Typography>
                <Label>Cell background color</Label>
                <ColourPicker label="Cell background colour" disabled={!editable} allowNone
                              value={cellBackground} onChange={onCellBackground}/>
            </Section>
        </Box>
    );
}
